import {
  ActionIcon,
  Card,
  DefaultMantineColor,
  Paper,
  Progress,
  Space,
  Stack,
  Text,
  TextInput,
  Textarea,
  Fieldset,
} from "@mantine/core"
import Dagre from "@dagrejs/dagre"
import ReactFlow, {
  useNodesState,
  useEdgesState,
  addEdge,
  Controls,
  Handle,
  Position,
  type Node,
  useReactFlow,
  OnConnect,
  BaseEdge,
  getSimpleBezierPath,
  EdgeProps,
  EdgeLabelRenderer,
  Panel,
} from "reactflow"
import "reactflow/dist/style.css"
import { AbsentApprovalNode } from "../../../../../@client/Absents"
import { match } from "ts-pattern"
import { IconX } from "@tabler/icons-react"
import {
  DepartmentRoleNodeContent,
  NotifyUsersPropertyEditors,
  ProductionRoleNodeContent,
  StaticApproversNodeContent,
} from "./approval-nodes-editors"
import { ApprovalConfigNodeProps, toFlowEdge, toTsEdge } from "./shared"
import { v4 as newGuid } from "uuid"
import { ConditionPropertiesEditor } from "./condition-nodes-editors"
import { DeleteButton } from "../../../../../-components/Buttons"
import { Guid } from "../../../../../@client/System"
import { FSharpList } from "../../../../../@client/Microsoft_FSharp_Collections"
import { DottedMenu } from "../../../../../-components/dotted-menu"
import { Edge as AbsenceEdge } from "../../../../../@client/Theasoft_Absences_Workflow.ts"
import { debugLog } from "@/utils/debugLog.tsx"
import {
  AbsentApprovalConfigNodeDetails,
  GraphNode as AbsenceGraphNode,
  AbsenceConfigValidationError,
} from "../../../../../@client/Theasoft_Absences_Config"
import { useTranslation } from "react-i18next"
import { SuspenseWithErrorBoundary } from "../../../../../-components/suspense"
import { useAbsenceConfigContext } from "./AbsenceContext.tsx"
import { useUpdateOrCreateApprovalConfigForm } from "./useUpdateOrCreateApprovalConfigForm.tsx"

type AbsentApprovalConfigNode =
  AbsenceGraphNode<AbsentApprovalConfigNodeDetails>

const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({ label: "" }))

const nodeWidth = 300
const nodeHeight = 300

const getLayoutedElements = (nodes: any, edges: any, options: any) => {
  g.setGraph({ rankdir: options.direction })

  edges.forEach((edge: any) => g.setEdge(edge.source, edge.target))
  nodes.forEach((node: any) =>
    g.setNode(node.id, { width: nodeWidth, height: nodeHeight })
  )

  Dagre.layout(g)

  return {
    nodes: nodes.map((node: any) => {
      const { x, y } = g.node(node.id)

      return { ...node, position: { x, y } }
    }),
    edges,
  }
}

type NodeType = "trigger" | "approval" | "condition"

function toFlowNode(
  node: AbsentApprovalConfigNode
): Node<AbsentApprovalConfigNodeDetails, NodeType> {
  return {
    id: node.nodeId,
    data: node.details,
    position: node?.position || { x: 0, y: 0 },
    type: "approval",
  }
}

function toTsNode(
  node: Node<AbsentApprovalConfigNodeDetails, NodeType>
): AbsentApprovalConfigNode {
  return {
    name: null,
    nodeId: node.id as Guid,
    details: node.data,
    position: node.position,
    description: null,
  }
}

type GraphNode = AbsentApprovalConfigNode | AbsentApprovalNode

type GraphEdge = AbsenceEdge
// TODO remove
type Graph = {
  nodes: GraphNode[]
  edges: GraphEdge[]
}

export function ConfigFlow({ data }: { data: Graph }) {
  const [form] = useUpdateOrCreateApprovalConfigForm()
  const initialNodes = form.values.nodes.map(toFlowNode)
  const initialEdges = data.edges.map(toFlowEdge)
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)
  const { fitView } = useReactFlow()
  const { t } = useTranslation("app_server")
  const onLayout = React.useCallback(
    (direction: "LR") => {
      const layouted = getLayoutedElements(nodes, edges, { direction })
      const nodes2 = [...layouted.nodes]
      form.setFieldValue("nodes", nodes2.map(toTsNode))
      setNodes(nodes2)
      setEdges([...layouted.edges])
      window.requestAnimationFrame(() => {
        fitView()
      })
    },
    [nodes, edges]
  )

  const { showIds, isReadonly } = useAbsenceConfigContext()

  const onConnect: OnConnect = React.useCallback(
    (params) => {
      debugLog("on connect")
      setEdges((eds) => {
        const { source, target } = params
        if (source === null || target === null) {
          throw new Error("source or target is null")
        }
        const edge: AbsenceEdge = {
          id: newGuid(),
          source: source as Guid,
          target: target as Guid,
        }
        const edges = addEdge({ ...edge, type: "custom" }, eds)
        form.setFieldValue(
          "edges",
          edges.map((v) => ({
            id: v.id as Guid,
            source: v.source as Guid,
            target: v.target as Guid,
          }))
        )

        return edges
      })
    },
    [setEdges, onLayout]
  )

  const nodeTypes = React.useMemo(() => ({ approval: ApprovalNode }), [])

  const edgeTypes = React.useMemo(() => ({ custom: CustomEdge }), [])

  function addNode(details: AbsentApprovalConfigNodeDetails) {
    const node: Node<AbsentApprovalConfigNodeDetails, NodeType> = {
      data: details,
      type: "approval",
      id: newGuid(),
      position: { x: 0, y: 0 },
    }

    setNodes((nodes) => {
      const result = [...nodes, node]
      form.setFieldValue("nodes", [...form.values.nodes, toTsNode(node)])
      return result
    })
  }

  const selectedNode = React.useMemo(() => {
    return nodes.find((v) => v.selected)
  }, [nodes])

  function deleteNode(nodeId: string) {
    // TODO: make nodes one source of truth (probably the form values)
    setNodes((nodes) => nodes.filter((v) => v.id !== nodeId))
    form.setFieldValue(
      "nodes",
      form.values.nodes.filter((v) => v.nodeId !== nodeId)
    )
  }
  return (
    <div
      style={{
        height: "800px",
        display: "grid",
        gridTemplateColumns: selectedNode == null ? "1fr" : "1fr 400px",
        gap: 12,
      }}
    >
      <Paper h="100%" withBorder p="xs">
        <ReactFlow
          snapToGrid
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeDragStop={(_e, node) => {
            const index = nodes.findIndex((v) => v.id === node.id)
            const existingNode = form.values.nodes[index]
            form.setFieldValue("nodes." + index, {
              ...existingNode,
              position: node.position,
            })
          }}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          fitView
        >
          <Panel position="top-left">
            <DottedMenu
              items={[
                {
                  children: t("AddStartEvent"),
                  disabled: isReadonly,
                  onClick: () => {
                    const details: AbsentApprovalConfigNodeDetails = {
                      Case: "Trigger",
                    }
                    addNode(details)
                  },
                },
                {
                  children: t("AddNotification"),
                  disabled: isReadonly,
                  onClick: () => {
                    const details: AbsentApprovalConfigNodeDetails = {
                      Case: "NotifyUsers",
                      Fields: { usersIds: [] },
                    }
                    addNode(details)
                  },
                },
                {
                  children: t("PersonOrUserApproval"),
                  disabled: isReadonly,
                  onClick: () => {
                    const details: AbsentApprovalConfigNodeDetails = {
                      Case: "StaticApproval",
                      Fields: {
                        noteForApprovers: null,
                        approvers: [],
                        canChangeFinalDecision: false,
                        rejectionIsFinal: false,
                      },
                    }
                    addNode(details)
                  },
                },
                {
                  children: t("ProductionRoleApproval"),
                  disabled: isReadonly,
                  onClick: () => {
                    const details: AbsentApprovalConfigNodeDetails = {
                      Case: "ProductionRoleApproval",
                      Fields: {
                        noteForApprovers: null,
                        roleTypeIds: [],
                        canChangeFinalDecision: false,
                        rejectionIsFinal: false,
                      },
                    }
                    addNode(details)
                  },
                },
                {
                  children: t("DepartmentRoleApproval"),
                  disabled: isReadonly,
                  onClick: () => {
                    const details: AbsentApprovalConfigNodeDetails = {
                      Case: "DepartmentRoleApproval",
                      Fields: {
                        workareaMembersWithFunctions: [],
                        departmentMembersWithFunctions: [],
                        includeRequestingPerson: false,
                        noteForApprovers: null,
                        rejectionIsFinal: false,
                        canChangeFinalDecision: false,
                      },
                    }
                    addNode(details)
                  },
                },
                {
                  children: t("DepartmentCondition"),
                  disabled: isReadonly,
                  onClick: () => {
                    const details: AbsentApprovalConfigNodeDetails = {
                      Case: "DepartmentOrWorkareaCondition",
                      Fields: { values: [] },
                    }
                    addNode(details)
                  },
                },
                {
                  children: t("ContractCondition"),
                  disabled: isReadonly,
                  onClick: () => {
                    const details: AbsentApprovalConfigNodeDetails = {
                      Case: "ContractTypeOrContractTypeCategoryCondition",
                      Fields: { values: [] },
                    }
                    addNode(details)
                  },
                },
              ]}
            />
          </Panel>
          <Controls />
        </ReactFlow>
      </Paper>
      {selectedNode ? (
        <Paper h="100%" withBorder p="xs" key={selectedNode.id}>
          <Fieldset disabled={isReadonly} variant="unstyled">
            <Stack gap="md" h="100%">
              <Text c="gray">{showIds ? selectedNode.id : null}</Text>
              <SuspenseWithErrorBoundary>
                <NodePropertyEditor key={selectedNode.id} node={selectedNode} />
                <div style={{ flex: 1 }} />
                <DeleteButton
                  disabled={isReadonly}
                  onClick={() => {
                    deleteNode(selectedNode.id)
                  }}
                />
              </SuspenseWithErrorBoundary>
            </Stack>
          </Fieldset>
          {/* <Debug _show nodes={nodes} /> */}
        </Paper>
      ) : null}
    </div>
  )
}

function ApprovalNodeCommonProperties({ id: nodeId }: { id: string }) {
  const [form, _] = useUpdateOrCreateApprovalConfigForm()

  const node = form.values.nodes.find((n) => n.nodeId === nodeId)
  const index = form.values.nodes.findIndex((n) => n.nodeId === nodeId)
  const __ = _.nodes[index]

  const { t } = useTranslation("app_server")

  if (node === undefined || index === undefined) {
    debugLog("nodeId not found", { nodeId, nodes: form.values.nodes })
    return <Text c="red">{t("CouldntFindX", { x: `nodeId ${nodeId}` })}</Text>
  }

  return (
    <>
      <TextInput
        label={t("Title")}
        placeholder={t("TitleOfApprovalStep")}
        {...form.getInputProps(__.name._PATH_)}
      />

      <Textarea
        {...form.getInputProps(__.description._PATH_)}
        description={t("ConfigurationDescriptionDescription")}
        placeholder={t("Description")}
        label={t("Description")}
      />
    </>
  )
}

function ConditionalNodeCommonProperties({ id: nodeId }: { id: string }) {
  const [form, _] = useUpdateOrCreateApprovalConfigForm()

  const node = form.values.nodes.find((n) => n.nodeId === nodeId)
  const index = form.values.nodes.findIndex((n) => n.nodeId === nodeId)
  const __ = _.nodes[index]

  const { t } = useTranslation("app_server")

  if (node === undefined || index === undefined) {
    debugLog("nodeId not found", { nodeId, nodes: form.values.nodes })
    return <Text c="red">{t("CouldntFindX", { x: `nodeID ${nodeId}` })}</Text>
  }
  return (
    <>
      <TextInput
        label={t("Title")}
        placeholder={t("TitleOfCondition")}
        description={t("NotShownInApprovalProcess")}
        {...form.getInputProps(__.name._PATH_)}
      />
      <Textarea
        {...form.getInputProps(__.description._PATH_)}
        description={t("ConfigurationDescriptionDescription")}
        label={t("Description")}
      />
    </>
  )
}

function NodePropertyEditor({
  node,
}: {
  node: Node<AbsentApprovalConfigNodeDetails, string | undefined>
}) {
  const { t } = useTranslation("app_server")
  return match(node.data)
    .with({ Case: "ContractTypeOrContractTypeCategoryCondition" }, () => (
      <>
        <ConditionalNodeCommonProperties id={node.id} />
        <ConditionPropertiesEditor
          nodeId={node.id}
          defaultMode="editing"
          allowEditing={true}
        />
      </>
    ))
    .with({ Case: "DepartmentOrWorkareaCondition" }, () => (
      <>
        <ConditionalNodeCommonProperties id={node.id} />
        <ConditionPropertiesEditor
          nodeId={node.id}
          defaultMode="editing"
          allowEditing={true}
        />
      </>
    ))
    .with({ Case: "Trigger" }, () => (
      <Text>{t("ThisElementIsNotEditable")}</Text>
    ))
    .with({ Case: "ProductionRoleApproval" }, () => (
      <>
        <ApprovalNodeCommonProperties id={node.id} />
        <ProductionRoleNodeContent {...node} />
      </>
    ))
    .with({ Case: "StaticApproval" }, () => (
      <>
        <ApprovalNodeCommonProperties id={node.id} />
        <StaticApproversNodeContent {...node} />
      </>
    ))
    .with({ Case: "NotifyUsers" }, () => (
      <>
        <ApprovalNodeCommonProperties id={node.id} />
        <NotifyUsersPropertyEditors {...node} />
      </>
    ))
    .with({ Case: "DepartmentRoleApproval" }, () => (
      <>
        <ApprovalNodeCommonProperties id={node.id} />
        <DepartmentRoleNodeContent {...node} />
      </>
    ))

    .exhaustive()
}

function CustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  selected,
}: EdgeProps) {
  const { setEdges } = useReactFlow()
  const [edgePath, labelX, labelY] = getSimpleBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })
  const [form, _] = useUpdateOrCreateApprovalConfigForm()
  const onClickRemoveEdge = () => {
    setEdges((edges) => {
      const result = edges.filter((edge) => edge.id !== id)
      const tsEdges: FSharpList<AbsenceEdge> = result.map(toTsEdge)
      form.setFieldValue("edges", tsEdges)
      return result
    })
  }
  const { isReadonly } = useAbsenceConfigContext()
  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: 12,
            // everything inside EdgeLabelRenderer has no pointer events by default
            // if you have an interactive element, set pointer-events: all
            pointerEvents: "all",
          }}
        >
          {selected && !isReadonly ? (
            <ActionIcon size="xs" variant="light" onClick={onClickRemoveEdge}>
              <IconX />
            </ActionIcon>
          ) : null}
        </div>
      </EdgeLabelRenderer>
    </>
  )
}

function TriggerNode({}: ApprovalConfigNodeProps) {
  const { isReadonly } = useAbsenceConfigContext()
  const { t } = useTranslation()
  return (
    <>
      <Paper
        bg="orange"
        c="white"
        radius="xl"
        withBorder
        px="md"
        shadow="xl"
        style={{ alignContent: "center", justifyItems: "center" }}
      >
        <Text>{t("AbsenceRequested")}</Text>
      </Paper>
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={!isReadonly}
      />
    </>
  )
}

function ApprovalNode(props: ApprovalConfigNodeProps) {
  const ctx = useAbsenceConfigContext()
  const errors = ctx.validationErrors?.filter(
    (v) => v.Case === "Node" && v.Fields.nodeId == props.id
  )
  const { t } = useTranslation("app_server")
  return match(props.data)
    .with({ Case: "DepartmentOrWorkareaCondition" }, () => (
      <NodeSkelet errors={errors} title={t("DepartmentCondition")} {...props} />
    ))
    .with({ Case: "ContractTypeOrContractTypeCategoryCondition" }, () => (
      <NodeSkelet errors={errors} title={t("ContractCondition")} {...props} />
    ))
    .with({ Case: "Trigger" }, () => <TriggerNode {...props} />)
    .with({ Case: "ProductionRoleApproval" }, () => (
      <NodeSkelet
        errors={errors}
        title={t("ProductionRoleApproval")}
        subtitle={t("Approval")}
        titleColor="violet"
        {...props}
      >
        {() => null}
      </NodeSkelet>
    ))
    .with({ Case: "StaticApproval" }, () => (
      <NodeSkelet
        errors={errors}
        title={t("Approvalstep")}
        subtitle={t("Approval")}
        titleColor="violet"
        {...props}
      >
        {() => null}
      </NodeSkelet>
    ))
    .with({ Case: "DepartmentRoleApproval" }, () => (
      <NodeSkelet
        errors={errors}
        title={t("DepartmentRoleApproval")}
        subtitle={t("Approval")}
        titleColor="violet"
        {...props}
      >
        {() => null}
      </NodeSkelet>
    ))
    .with({ Case: "NotifyUsers" }, () => (
      <NodeSkelet
        errors={errors}
        title={t("Notification")}
        subtitle="Info"
        titleColor="yellow"
        {...props}
      >
        {() => null}
      </NodeSkelet>
    ))
    .exhaustive()
}

function NodeSkelet({
  children,
  title,
  subtitle,
  titleColor,
  sourceHandle,
  targetHandle,
  showAsActive,
  errors,
  ...props
}: {
  errors?: AbsenceConfigValidationError[] | null | undefined
  titleColor?: DefaultMantineColor
  subtitle?: string
  title: string
  children?: (editing: boolean) => React.ReactNode | React.ReactNode[]
  sourceHandle?: boolean
  targetHandle?: boolean
  showAsActive?: boolean
} & ApprovalConfigNodeProps) {
  const { showIds, isReadonly } = useAbsenceConfigContext()
  const [form, _] = useUpdateOrCreateApprovalConfigForm()
  const index = form.values.nodes.findIndex((v) => v.nodeId === props.id)
  const node = form.values.nodes[index]
  const hasErrors = errors ? errors.length > 0 : false
  if (index === -1 || node === undefined) {
    return <div>Node not found in form values</div>
  }

  return (
    <>
      {targetHandle === false ? null : (
        <Handle
          isConnectable={!isReadonly}
          type="target"
          position={Position.Left}
        />
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1f auto",
          gridTemplateRows: "auto auto auto",
          gridTemplateAreas: `
            "top top-right"
            "center right"
            "bottom bottom-right"`,
          alignContent: "stretch",
          justifyItems: "stretch",
        }}
      >
        <Card
          style={{
            width: nodeWidth,
            gridArea: "center",
          }}
          withBorder
          styles={(theme) =>
            props.selected
              ? {
                  root: {
                    borderColor: hasErrors
                      ? theme.colors.red[8]
                      : theme.colors.gray[7],
                  },
                }
              : {
                  root: {
                    borderColor: hasErrors ? theme.colors.red[6] : undefined,
                  },
                }
          }
          shadow="xs"
        >
          {showIds ? (
            <Text c="gray" size="xs">
              {node.nodeId}
            </Text>
          ) : null}
          {showAsActive === true ? (
            <Card.Section p={0}>
              <Progress
                color="blue"
                size="xs"
                value={100}
                animated
                radius={0}
              />
            </Card.Section>
          ) : null}

          <Card.Section p="md">
            <TextInput
              readOnly={isReadonly}
              description={subtitle}
              c="pink"
              color="pink"
              styles={(theme) => ({
                input: {
                  fontWeight: "bold",
                  border: "none",
                  paddingLeft: 0,
                  color: titleColor ? theme.colors[titleColor][4] : undefined,
                },
              })}
              onChange={(e) => {
                form.setFieldValue("nodes." + index + ".name", e.target.value)
              }}
              value={node.name || title}
            />

            <Space h={"xs"} />
            {children ? children(true) : null}
          </Card.Section>
        </Card>
      </div>
      {sourceHandle === false ? null : (
        <Handle
          isConnectable={!isReadonly}
          type="source"
          position={Position.Right}
        />
      )}
    </>
  )
}
